<template>
  <nav class="v2-layout-header navbar navbar-expand-lg" :class="[theme, theme === V2_HEADER_THEME_DARK ? 'navbar-dark' : 'navbar-light']">
    <div class="container">
      <component :is="route.path == '/' ? 'div' : 'router-link'" to="/" class="navbar-brand">
        <img :src="logo" width="103" height="43" :alt="`Ersties Logo`" />
      </component>

      <div class="togglers d-lg-none">
        <a
          v-modal.modal-signup
          href="#"
          class="btn btn-primary btn-sm me-2"
          :class="{ 'btn-outline-dark': theme !== V2_HEADER_THEME_DARK }"
          @click="sendRegistrationTrack('join')"
          >{{ t("Join now") }}</a
        >
        <button
          class="navbar-toggler"
          data-bs-toggle="collapse"
          data-bs-target="#navbar-content"
          aria-controls="navbar-content"
          :aria-label="`Menu open`"
        >
          <i class="icon icon-toggler" />
        </button>
      </div>
      <div id="navbar-content" ref="navbarContent" class="collapse navbar-collapse">
        <div class="horizontal-menu d-none d-lg-flex flex-fill">
          <div class="navbar-nav m-auto primary-menu">
            <div v-for="(item, i) in menuItems" :key="i" class="nav-link">
              <router-link
                :to="item.url"
                :active-class="item.url.includes('#') ? '' : 'active'"
                class="btn btn-outline-light btn-sm"
                @click="sendMenuTrack(item?.name as string)"
              >
                {{ item.anchor }}
              </router-link>
            </div>
          </div>
          <div class="navbar-nav secondary-menu">
            <role-bar v-if="roles.length" :v2="true" />
            <language-dropdown class="nav-link" toggler-class="btn btn-outline-light btn-sm" />
            <div class="nav-link">
              <a
                v-modal.modal-login
                href="#"
                class="btn btn-outline-light btn-sm"
                :class="theme === V2_HEADER_THEME_DARK ? 'btn-outline-light' : 'btn-outline-dark'"
                >{{ t("Sign In") }}
              </a>
            </div>
            <div class="nav-link">
              <a
                v-modal.modal-signup
                href="#"
                class="btn btn-primary btn-sm"
                :class="{ 'btn-outline-dark': theme !== V2_HEADER_THEME_DARK }"
                @click="sendRegistrationTrack('signup')"
                >{{ t("Join now") }}
              </a>
            </div>
          </div>
        </div>

        <div class="vertical-menu container-fluid d-block d-lg-none">
          <div class="d-flex justify-content-between">
            <component :is="route.path == '/' ? 'div' : 'router-link'" to="/" class="navbar-brand d-block d-lg-none">
              <img :src="logo" width="103" height="43" :alt="`Ersties Logo`" />
            </component>

            <button
              class="navbar-toggler"
              data-bs-toggle="collapse"
              data-bs-target="#navbar-content"
              aria-controls="navbar-content"
              :aria-label="`Menu close`"
            >
              <i class="icon icon-cross" />
            </button>
          </div>
          <div class="navbar-nav mt-4">
            <div class="primary-menu mb-2">
              <router-link
                v-for="(item, i) in menuItems"
                :key="i"
                :to="item.url"
                :active-class="item.url.startsWith('/#') ? '' : 'active'"
                class="nav-link"
                @click="onLinkClick(item?.name as string)"
              >
                {{ item.anchor }}
              </router-link>
            </div>
            <div class="secondary-menu">
              <div class="d-flex justify-content-center mb-1">
                <a v-modal.modal-login class="btn btn-info" :class="{ 'btn-outline-dark': theme !== V2_HEADER_THEME_DARK }">{{ t("Log in") }}</a>
              </div>
              <div class="d-flex justify-content-center">
                <a
                  v-modal.modal-signup
                  href="#"
                  class="btn btn-primary"
                  :class="{ 'btn-outline-dark': theme !== V2_HEADER_THEME_DARK }"
                  @click="sendRegistrationTrack('signup')"
                  >{{ t("Sign up") }}</a
                >
              </div>
              <social-buttons />
              <div class="text-center">
                <language-dropdown />
              </div>
              <div class="text-center">
                <role-bar v-if="roles.length" :v2="true" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, onBeforeMount, PropType, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import Collapse from "bootstrap/js/src/collapse";
import SocialButtons from "@/components/v2/social-buttons";
import { amplitudeRegistrationButtonClickTrack, amplitudeTopMenuClickTrack } from "@/utils/amplitude";
import LanguageDropdown from "@/components/v2/language-dropdown";
import { V2_HEADER_THEME_DARK } from "@/utils/variables";
import { MenuItem } from "@/interfaces/MenuItem.ts";
import { useRoleStore } from "@/store/role.ts";
import logoDe from "@/assets/v2/images/logo-de.svg";
import logoEn from "@/assets/v2/images/logo-en.svg";

defineOptions({
  name: "layout-header",
});

defineProps({
  menuItems: {
    type: Array as PropType<MenuItem[]>,
    required: true,
  },
});

const route = useRoute();
const { locale, t } = useI18n();
const roleStore = useRoleStore();

const navbarContent = ref<HTMLDivElement | null>(null);
const theme = ref(V2_HEADER_THEME_DARK);

const logo = computed(() => (locale.value === "de" ? logoDe : logoEn));

const RoleBar = defineAsyncComponent(() => import("@/components/role-bar"));
const roles = computed(() => roleStore.getAll);

const sendMenuTrack = (name: string) => {
  amplitudeTopMenuClickTrack(name);
};

const sendRegistrationTrack = (name: string) => {
  amplitudeRegistrationButtonClickTrack(`top menu${name}`);
};

const onLinkClick = (name: string) => {
  Collapse.getInstance(navbarContent.value).hide();
  sendMenuTrack(name);
};

onBeforeMount(() => {
  theme.value = (route.meta?.v2headerTheme || theme.value) as string;
});
</script>

<style lang="scss">
@import "./index.scss";
</style>
